import { Injectable, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';

export interface BreadcrumbItem {
    label: string;
    routerLink?: string;
    translationKey?: string;
}

@Injectable({ providedIn: 'root' })
export class BreadcrumbService {
    private router = inject(Router);

    private readonly _breadcrumbs$ = new BehaviorSubject<BreadcrumbItem[]>([]);
    readonly breadcrumbs$ = this._breadcrumbs$.asObservable();
    private _manuallySet = false;

    constructor() {
        this.router.events
            .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
            .subscribe(() => {
                this._manuallySet = false;
                // Defer so component ngOnInit can call set() first
                setTimeout(() => {
                    if (!this._manuallySet) {
                        this._autoSet();
                    }
                });
            });
    }

    set(breadcrumbs: BreadcrumbItem[]): void {
        this._manuallySet = true;
        this._breadcrumbs$.next(breadcrumbs);
    }

    clear(): void {
        this._breadcrumbs$.next([]);
    }

    /** Update the last breadcrumb's label (e.g. replace "..." with entity name after data loads). */
    updateLastLabel(label: string, routerLink?: string): void {
        const crumbs = [...this._breadcrumbs$.getValue()];
        if (crumbs.length > 0) {
            const updated: BreadcrumbItem = { label };
            if (routerLink !== undefined) {
                updated.routerLink = routerLink;
            } else if (crumbs[crumbs.length - 1].routerLink) {
                updated.routerLink = crumbs[crumbs.length - 1].routerLink;
            }
            crumbs[crumbs.length - 1] = updated;
            this._breadcrumbs$.next(crumbs);
        }
    }

    /** Returns a snapshot of current breadcrumbs (for passing to child navigation state). */
    getCurrentBreadcrumbs(): BreadcrumbItem[] {
        return [...this._breadcrumbs$.getValue()];
    }

    private _autoSet(): void {
        let route = this.router.routerState.root;
        while (route.firstChild) {
            route = route.firstChild;
        }

        // Support nested breadcrumbs via route data 'breadcrumbs' array
        const breadcrumbsData = route.snapshot.data['breadcrumbs'] as
            { translationKey: string; routerLink?: string }[] | undefined;

        if (breadcrumbsData?.length) {
            const crumbs: BreadcrumbItem[] = [
                { label: 'Dashboard', routerLink: '/', translationKey: 'dashboard' },
            ];
            for (const b of breadcrumbsData) {
                crumbs.push({ label: b.translationKey, translationKey: b.translationKey, routerLink: b.routerLink });
            }
            this._breadcrumbs$.next(crumbs);
            return;
        }

        const breadcrumbKey = route.snapshot.data['breadcrumb'] as string | undefined;
        if (breadcrumbKey) {
            this._breadcrumbs$.next([
                { label: 'Dashboard', routerLink: '/', translationKey: 'dashboard' },
                { label: breadcrumbKey, translationKey: breadcrumbKey },
            ]);
        } else if (route.snapshot.url.length === 0) {
            // Home route
            this._breadcrumbs$.next([
                { label: 'Dashboard', translationKey: 'dashboard' },
            ]);
        }
    }
}
